import type { ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import { RoutePutInputsProducts } from "@CommonApi/inputs/products";
import {
  Product,
  ProductSortableColumn,
  ResponseGetInputsProductsAll,
  RouteGetInputsProductsAll,
} from "@CommonApi/inputs/products/all";
import {
  CellClassParams,
  CellValueChangedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
} from "ag-grid-community";
import { cloneDeep, isEqual } from "es-toolkit";
import get from "es-toolkit/compat/get";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "src/api";
import { useMerchflowsApi } from "src/api/merchflows";
import { AgGridTable } from "src/components/AgGridTable";
import { useAgGridLayoutPersistence } from "src/components/AgGridTable/store/layoutPersistence";
import { Page } from "src/components";
import { BackendSortHeader } from "src/components/AgGridTable/headers/BackendSortHeader";
import { useLoaders } from "src/components/InlineLoader";
import { useModals } from "src/components/Modals";
import { PRODUCTS_DEFAULT_PAGE_SIZE } from "src/constants";
import { Button, Color, Flex, Pagination, Scroller, Text } from "src/elements";
import { useAsyncState } from "src/hooks/useAsyncState";
import { useSearchParams } from "src/utils/useSearchParams";
import {
  gridFilterModelToQueryParams,
  queryParamsToGridFilterModel,
} from "./store/agGridModelMapper";
import {
  buildProductsFilterSearchParams,
  filterMultiSelectValues,
  getAvailableProductSubcategoryOptions,
  getProductsCategoryOptions,
  getProductsSubcategoryOptions,
  parseCsvSearchParam,
  type ProductsSearchParamsInput,
  serializeCsvSearchParam,
} from "./store/filters";
import {
  EmptyProductOverlay,
  getColumns,
  getRowStyle,
  LoadingProductOverlay,
} from "./store/helper";
import { getNextProductsSort } from "./store/sort";

const productsGridLayoutStorageKey = "products-table-layout:v1";
const emptyCategoriesMap: ResponseGetMerchflowsCategories = {};

const normalizeSearchParams = (params: ProductsSearchParamsInput) => {
  const cleanSearchParams: { [key: string]: string } = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || (key === "page" && (value === 1 || value === "1"))) {
      continue;
    } else if (typeof value === "number") {
      cleanSearchParams[key] = String(value);
    } else if (typeof value === "string" && value !== "") {
      cleanSearchParams[key] = value;
    }
  }

  return cleanSearchParams;
};

const areSearchParamsEqual = (
  currentParams: ProductsSearchParamsInput,
  nextParams: ProductsSearchParamsInput,
) => {
  const current = normalizeSearchParams(currentParams);
  const next = normalizeSearchParams(nextParams);

  const currentKeys = Object.keys(current) as (keyof typeof current)[];
  const nextKeys = Object.keys(next) as (keyof typeof next)[];

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return currentKeys.every((key) => current[key] === next[key]);
};

const toPositiveNumberSearchParam = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
};

export const PageProducts = () => {
  const { getInputsProductsAllApi, putInputsProductsApi } = useApi();
  const { getMerchflowsCategoriesApi } = useMerchflowsApi();
  const { hasLoader } = useLoaders();
  const { openConfirmModal } = useModals();
  const { searchParams, setSearchParams } = useSearchParams();
  const { persistGridLayout, restoreGridLayout } = useAgGridLayoutPersistence<Product>(
    productsGridLayoutStorageKey,
  );

  const [availableCategories, setAvailableCategories] =
    useState<ResponseGetMerchflowsCategories | null>(null);
  const [products, setProducts] = useAsyncState<ResponseGetInputsProductsAll>();
  const [productMap, setProductMap] = useState<{ [key: string]: Product }>({});
  const [modifiedProducts, setModifiedProducts] = useState<Product[]>([]);

  const getMerchflowsCategoriesApiRef = useRef(getMerchflowsCategoriesApi);
  const gridApiRef = useRef<GridApi<Product> | null>(null);
  const isProgrammaticGridSyncRef = useRef(false);
  const gridSyncTimeoutRef = useRef<number | undefined>(undefined);

  const currentSearchParams = useMemo(() => {
    const nextSearchParams = { ...searchParams } as ProductsSearchParamsInput & {
      search?: string;
    };
    delete nextSearchParams.search;
    return nextSearchParams;
  }, [searchParams]);
  const page = toPositiveNumberSearchParam(searchParams["page"]) || 1;
  const upc = searchParams["upc"] || undefined;
  const productCode = searchParams["product_code"] || undefined;
  const name = searchParams["name"] || undefined;
  const brand = searchParams["brand"] || undefined;
  const category = useMemo(
    () => parseCsvSearchParam(searchParams["category"]),
    [searchParams["category"]],
  );
  const rawSubcategory = useMemo(
    () => parseCsvSearchParam(searchParams["subcategory"]),
    [searchParams["subcategory"]],
  );
  const sortBy = searchParams["sortBy"] as ProductSortableColumn | undefined;
  const sortDirection = searchParams["sortDirection"] as "asc" | "desc" | undefined;
  const availableCategoriesMap = availableCategories || emptyCategoriesMap;
  const canValidateSubcategories = availableCategories !== null;
  const categoryOptions = useMemo(
    () => getProductsCategoryOptions(availableCategoriesMap),
    [availableCategoriesMap],
  );
  const subcategoryOptions = useMemo(
    () => getProductsSubcategoryOptions(availableCategoriesMap, category),
    [availableCategoriesMap, category],
  );
  const subcategory = useMemo(
    () =>
      canValidateSubcategories
        ? filterMultiSelectValues(
            rawSubcategory,
            getAvailableProductSubcategoryOptions(availableCategoriesMap, category),
          )
        : rawSubcategory,
    [availableCategoriesMap, canValidateSubcategories, category, rawSubcategory],
  );

  const isLoading = products === undefined || hasLoader(RouteGetInputsProductsAll);
  const isSaveLoading = hasLoader(RoutePutInputsProducts);

  useEffect(() => {
    getMerchflowsCategoriesApiRef.current = getMerchflowsCategoriesApi;
  }, [getMerchflowsCategoriesApi]);

  useEffect(() => {
    let isCancelled = false;

    const loadCategories = async () => {
      const response = await getMerchflowsCategoriesApiRef.current();

      if (isCancelled || response === null) {
        return;
      }

      setAvailableCategories(response);
    };

    void loadCategories();

    return () => {
      isCancelled = true;
    };
  }, []);

  const applySearchParams = useCallback(
    (nextSearchParams: ProductsSearchParamsInput) => {
      if (areSearchParamsEqual(currentSearchParams, nextSearchParams)) {
        return;
      }

      setSearchParams(nextSearchParams);
    },
    [currentSearchParams, setSearchParams],
  );

  const loadProducts = useCallback(async () => {
    const response = await getInputsProductsAllApi({
      page,
      size: PRODUCTS_DEFAULT_PAGE_SIZE,
      upc,
      product_code: productCode,
      name,
      brand,
      category,
      subcategory,
      sortBy,
      sortDirection,
    });

    setProductMap(
      response
        ? response.data.reduce(
            (acc, product) => {
              acc[product.product_id] = product;
              return acc;
            },
            {} as { [key: string]: Product },
          )
        : {},
    );
    setProducts(cloneDeep(response));
  }, [brand, category, name, page, productCode, sortBy, sortDirection, subcategory, upc]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(
    () => () => {
      if (gridSyncTimeoutRef.current !== undefined) {
        window.clearTimeout(gridSyncTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (searchParams["search"] === undefined) {
      return;
    }

    applySearchParams(currentSearchParams);
  }, [applySearchParams, currentSearchParams, searchParams]);

  useEffect(() => {
    if (!canValidateSubcategories) {
      return;
    }

    const serializedRawSubcategory = serializeCsvSearchParam(rawSubcategory);
    const serializedSubcategory = serializeCsvSearchParam(subcategory);

    if (serializedRawSubcategory === serializedSubcategory) {
      return;
    }

    applySearchParams({
      ...currentSearchParams,
      subcategory: serializedSubcategory,
    });
  }, [
    applySearchParams,
    canValidateSubcategories,
    currentSearchParams,
    rawSubcategory,
    subcategory,
  ]);

  const saveProducts = async () => {
    const response = await putInputsProductsApi({
      products: modifiedProducts,
    });

    if (response) {
      openConfirmModal({
        icon: { name: "checkmarkCircle" },
        title: "Products changes have been saved!",
        buttons: [{ id: "confirm", label: "Confirm" }],
      });
      clearModifiedProducts();
      await loadProducts();
    }
  };

  const clearModifiedProducts = () => {
    setModifiedProducts([]);
  };

  const defaultColDef = useMemo(
    (): ColDef<Product> => ({
      sortable: false,
      filter: false,
      resizable: true,
      editable: true,
      cellStyle: (params: CellClassParams<Product>) => {
        if (
          params.colDef.field &&
          params.value !== undefined &&
          !isEqual(
            params.value,
            get(productMap, `${params.data?.product_id}.${params.colDef.field}`),
          )
        ) {
          return { backgroundColor: Color.yellow };
        }
        return null;
      },
    }),
    [productMap],
  );

  const handleSortRequested = useCallback(
    (sortKey: ProductSortableColumn) => {
      const nextSort = getNextProductsSort(
        {
          sortBy,
          sortDirection,
        },
        sortKey,
      );

      applySearchParams({
        ...currentSearchParams,
        page: 1,
        sortBy: nextSort.sortBy,
        sortDirection: nextSort.sortDirection,
      });
    },
    [applySearchParams, currentSearchParams, sortBy, sortDirection],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      applySearchParams({
        ...currentSearchParams,
        page: nextPage,
      });
    },
    [applySearchParams, currentSearchParams],
  );

  const getSortableHeaderParams = useCallback(
    (sortKey: ProductSortableColumn) => ({
      innerHeaderComponent: BackendSortHeader,
      innerHeaderComponentParams: {
        sortKey,
        activeSortBy: sortBy,
        activeSortDirection: sortDirection,
        onSortChange: handleSortRequested,
      },
    }),
    [handleSortRequested, sortBy, sortDirection],
  );

  const syncGridFiltersFromQueryParams = useCallback(
    (api: GridApi<Product>) => {
      isProgrammaticGridSyncRef.current = true;

      api.setFilterModel(
        queryParamsToGridFilterModel({
          upc,
          product_code: productCode,
          name,
          brand,
          category,
          subcategory,
        }),
      );

      if (gridSyncTimeoutRef.current !== undefined) {
        window.clearTimeout(gridSyncTimeoutRef.current);
      }

      gridSyncTimeoutRef.current = window.setTimeout(() => {
        isProgrammaticGridSyncRef.current = false;
      }, 0);
    },
    [brand, category, name, productCode, subcategory, upc],
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent<Product>) => {
      gridApiRef.current = event.api;
      restoreGridLayout(event.api);
      syncGridFiltersFromQueryParams(event.api);
    },
    [restoreGridLayout, syncGridFiltersFromQueryParams],
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }

    syncGridFiltersFromQueryParams(api);
  }, [syncGridFiltersFromQueryParams, categoryOptions, subcategoryOptions]);

  const handleFilterChanged = useCallback(() => {
    if (isProgrammaticGridSyncRef.current) {
      return;
    }

    const api = gridApiRef.current;
    if (!api) {
      return;
    }

    const filterQueryParams = gridFilterModelToQueryParams(api.getFilterModel());

    applySearchParams(
      buildProductsFilterSearchParams({
        currentSearchParams,
        filterQueryParams,
        availableCategories: availableCategoriesMap,
        canValidateSubcategories,
      }),
    );
  }, [applySearchParams, availableCategoriesMap, canValidateSubcategories, currentSearchParams]);

  const columns = useMemo(
    () => getColumns(getSortableHeaderParams, categoryOptions, subcategoryOptions),
    [categoryOptions, getSortableHeaderParams, subcategoryOptions],
  );

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Product>) => {
      if (!isEqual(event.data, productMap[event.data.product_id])) {
        setModifiedProducts((prev) => {
          const unchangedProducts = prev.filter(
            (item) => item.product_id !== event.data.product_id,
          );

          return unchangedProducts.concat(event.data);
        });
      } else {
        setModifiedProducts((prev) =>
          prev.filter((item) => item.product_id !== event.data.product_id),
        );

        // redraw when undo/redo only
        event.api.redrawRows();
      }
    },
    [productMap],
  );

  return (
    <Page>
      <Flex column flexGrow gap="10px">
        <Flex padding="10px 0 0 0" justify="between" align="center">
          <Text variant="h3">Products</Text>
        </Flex>

        <Flex justify="right">
          <Button width="120px" iconRight={{ name: "plus" }} isDisabled>
            Add Product
          </Button>
        </Flex>

        <Flex column flexGrow gap="20px">
          <Scroller>
            <AgGridTable
              columnDefs={columns}
              rowData={products?.data}
              loadingOverlayComponent={LoadingProductOverlay}
              loading={isLoading}
              noRowsOverlayComponent={EmptyProductOverlay}
              onCellValueChanged={onCellValueChanged}
              defaultColDef={defaultColDef}
              getRowStyle={getRowStyle}
              onGridReady={onGridReady}
              onFilterChanged={handleFilterChanged}
              onColumnMoved={(event) => {
                if (event.finished) {
                  persistGridLayout(event.api);
                }
              }}
              onColumnPinned={(event) => persistGridLayout(event.api)}
              onColumnVisible={(event) => persistGridLayout(event.api)}
              onColumnGroupOpened={(event) => persistGridLayout(event.api)}
              onColumnResized={(event) => {
                if (event.finished) {
                  persistGridLayout(event.api);
                }
              }}
            />
          </Scroller>
          {modifiedProducts.length > 0 && (
            <Flex justify="right">
              <Button width="120px" isLoading={isSaveLoading} onClick={saveProducts}>
                Save Changes
              </Button>
            </Flex>
          )}
        </Flex>

        <Pagination
          page={page}
          setPage={setPage}
          totalPages={products?.totalPages || 1}
          isLoading={isLoading}
          isUsingInput
        />
      </Flex>
    </Page>
  );
};
